import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Crown, Mail, Lock } from 'lucide-react';

// SC Brühl Logo
const SC_BRUEHL_LOGO = 'https://scbruehl.ch/wp-content/themes/scbruehl/assets/img/scb_logo.svg';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const userData = await login(email, password);
            toast.success('Willkommen zurück!');
            navigate(userData.is_admin ? '/dashboard' : '/');
        } catch (error) {
            const message = error.response?.data?.detail || 'Anmeldung fehlgeschlagen';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-background min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-scale-in">
                <div className="text-center mb-8">
                    <img 
                        src={SC_BRUEHL_LOGO} 
                        alt="SC Brühl St. Gallen" 
                        className="w-20 h-20 mx-auto mb-4"
                    />
                    <h1 className="text-4xl font-heading text-white tracking-tight">Kronentipp</h1>
                    <p className="text-green-200 mt-2 font-body">SC Brühl St. Gallen - Tippspiel</p>
                </div>

                <Card className="glass-card border-white/10">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl text-center">Anmelden</CardTitle>
                        <CardDescription className="text-center">
                            Melde dich an, um deine Tipps abzugeben
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        data-testid="login-email-input"
                                        type="email"
                                        placeholder="deine@email.ch"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 h-12"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Passwort</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        data-testid="login-password-input"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 h-12"
                                        required
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                data-testid="login-submit-button"
                                className="w-full btn-primary"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="spinner" />
                                        Anmelden...
                                    </span>
                                ) : (
                                    'Anmelden'
                                )}
                            </Button>
                        </form>

                        <div className="mt-3 text-center">
                            <Link 
                                to="/forgot-password" 
                                className="text-sm text-muted-foreground hover:text-primary hover:underline"
                                data-testid="forgot-password-link"
                            >
                                Passwort vergessen?
                            </Link>
                        </div>

                        <div className="mt-4 text-center text-sm">
                            <span className="text-muted-foreground">Noch kein Konto? </span>
                            <Link 
                                to="/register" 
                                className="text-primary hover:underline font-medium"
                                data-testid="register-link"
                            >
                                Jetzt registrieren
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
